import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import {
  useServicesForStaff,
  useServiceCategoriesForStaff,
} from "@/hooks/services/use-services-for-staff";
import { withStaffForm } from "@/hooks/staff/use-staff-form";
import { getStaffFormValues } from "@/default-form-values/staff/default-staff-form-values";
import { staffFormSchema } from "@/zod-schemas/staff/add-staff-schema";
import { Info } from "lucide-react";

interface StaffServicesStepProps {
  onBack: () => void;
}

export const StaffServicesStep = withStaffForm({
  defaultValues: getStaffFormValues(),
  validators: {
    onChange: staffFormSchema,
    onSubmit: staffFormSchema,
  },
  props: {} as StaffServicesStepProps,
  render: function StaffServicesStepRender(props) {
    const { form, onBack } = props;
    const {
      data: services = [],
      isLoading: servicesLoading,
      error: servicesError,
    } = useServicesForStaff();
    const {
      data: categories = [],
      isLoading: categoriesLoading,
      error: categoriesError,
    } = useServiceCategoriesForStaff();

    const getServicesForCategory = (categoryId: number) => {
      return services.filter((service) => service.category_id === categoryId);
    };

    if (servicesLoading || categoriesLoading) {
      return (
        <div className="space-y-6">
          <div className="flex justify-center py-8">
            <Spinner className="size-6" />
          </div>
          <form.Subscribe
            selector={(state) => ({
              hasSelectedServices: state.values.services.length > 0,
              isSubmitting: state.isSubmitting,
            })}
            children={({ hasSelectedServices, isSubmitting }) => (
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !hasSelectedServices}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </Button>
              </div>
            )}
          />
        </div>
      );
    }

    if (servicesError || categoriesError) {
      return (
        <div className="space-y-6">
          <div className="text-center py-8">
            <p className="text-destructive">
              Error loading services. Please contact support.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button type="submit" disabled>
              Save
            </Button>
          </div>
        </div>
      );
    }

    if (categories.length === 0) {
      return (
        <div className="space-y-6">
          <Card className="w-full border-l-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Info className="h-6 w-6" />
                You don&apos;t have any services yet
              </CardTitle>
            </CardHeader>
            <CardContent className="ml-8 text-muted-foreground">
              Add some on the Services page before assigning them to staff.
            </CardContent>
          </Card>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <form.Field
          name="services"
          children={(field) => {
            const selectedServices = field.state.value;

            const toggleService = (serviceId: number) => {
              const updatedServices = selectedServices.includes(serviceId)
                ? selectedServices.filter((id) => id !== serviceId)
                : [...selectedServices, serviceId];

              field.handleChange(updatedServices);
            };

            return (
              <FieldGroup>
                <div className="space-y-6 max-h-[400px] overflow-y-auto">
                  {categories.map((category) => {
                    const categoryServices = getServicesForCategory(category.id);

                    if (categoryServices.length === 0) return null;

                    return (
                      <div key={category.id} className="space-y-3">
                        <div className="font-semibold">{category.name}</div>

                        <div className="grid grid-cols-2 gap-3">
                          {categoryServices.map((service) => (
                            <Field key={service.id}>
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`service-${service.id}`}
                                  checked={selectedServices.includes(
                                    service.id,
                                  )}
                                  onCheckedChange={() =>
                                    toggleService(service.id)
                                  }
                                />
                                <FieldLabel
                                  htmlFor={`service-${service.id}`}
                                  className="text-sm font-normal cursor-pointer"
                                >
                                  {service.name}
                                </FieldLabel>
                              </div>
                            </Field>
                          ))}
                        </div>

                        {category !== categories[categories.length - 1] && (
                          <Separator className="my-4" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {!field.state.meta.isValid && (
                  <FieldError errors={field.state.meta.errors} />
                )}
              </FieldGroup>
            );
          }}
        />

        <form.Subscribe
          selector={(state) => ({
            hasSelectedServices: state.values.services.length > 0,
            isSubmitting: state.isSubmitting,
          })}
          children={({ hasSelectedServices, isSubmitting }) => (
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !hasSelectedServices}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
        />
      </div>
    );
  },
});
